import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "next-themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Ban, CheckCircle2, Clock3, CreditCard, Truck, XCircle } from "lucide-react";

import { NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";
import { cn } from "@/lib/utils";

type OrderStatus = "pending" | "paid" | "cancelled";

type ShippingStatus = "payment_pending" | "preparing" | "shipped" | "delivered" | "cancelled";

type Order = {
  id: string;
  createdAt: number;
  status: OrderStatus;
  paidAt?: number;
  accessToken: string;
  customer: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    identityNumber?: string;
    addressLine1?: string;
    city?: string;
    district?: string;
    postcode?: string;
    billingAddress?: {
      addressLine1: string;
      city: string;
      district?: string;
      postcode?: string;
    };
    shippingAddress?: {
      addressLine1: string;
      city: string;
      district?: string;
      postcode?: string;
    };
  };
  items: Array<
    | { kind: "package"; packageKey: string; qty: 1; unitPriceTry: number }
    | { kind: "product"; productId: string; qty: number; unitPriceTry: number }
  >;
  totalTry: number;
  shipping?: {
    status: ShippingStatus;
    trackingNumber?: string;
    carrier?: string;
    note?: string;
    updatedAt: number;
  };
  payment?: {
    provider: "iyzico";
    token: string;
    conversationId: string;
    paymentPageUrl?: string;
    lastStatus?: string;
    lastError?: string;
    lastCheckedAt?: number;
  };
};

type OrdersResponse = { ok: true; items: Order[] };

type Request = {
  id: string;
  orderId: string;
  status: "created" | "processing" | "sent" | "failed";
  createdAt: number;
  ad: string;
  soyad: string;
  email: string;
  packageKey: string;
};

type RequestsResponse = { ok: true; items: Request[] };

async function fetchOrders(): Promise<OrdersResponse> {
  const res = await adminFetch("/api/admin/numeroloji/orders");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(typeof json?.error === "string" ? json.error : "Siparişler alınamadı.");
  return json as OrdersResponse;
}

async function fetchRequests(): Promise<RequestsResponse> {
  const res = await adminFetch("/api/admin/numeroloji/requests");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(typeof json?.error === "string" ? json.error : "İstekler alınamadı.");
  return json as RequestsResponse;
}

async function cancelOrder(id: string) {
  const res = await adminFetch(`/api/admin/numeroloji/orders/${encodeURIComponent(id)}/cancel`, {
    method: "POST",
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(typeof json?.error === "string" ? json.error : "Sipariş iptal edilemedi.");
  return json as { ok: true; order: Order };
}

async function markPaid(id: string) {
  const res = await adminFetch(`/api/admin/numeroloji/orders/${encodeURIComponent(id)}/paid`, {
    method: "POST",
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(typeof json?.error === "string" ? json.error : "Güncellenemedi.");
  return json as { ok: true; order: Order };
}

async function updateShipping(input: {
  id: string;
  status: ShippingStatus;
  trackingNumber?: string;
  carrier?: string;
}) {
  const res = await adminFetch(`/api/admin/numeroloji/orders/${encodeURIComponent(input.id)}/shipping`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      status: input.status,
      trackingNumber: input.trackingNumber,
      carrier: input.carrier,
    }),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(typeof json?.error === "string" ? json.error : "Kargo durumu güncellenemedi.");
  return json as { ok: true; order: Order };
}

async function deleteOrder(id: string) {
  const res = await adminFetch(`/api/admin/numeroloji/orders/${encodeURIComponent(id)}/delete`, {
    method: "POST",
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(typeof json?.error === "string" ? json.error : "Sipariş silinemedi.");
  return json as { ok: true; deleted: true };
}

function formatDate(seconds: number) {
  try {
    return new Intl.DateTimeFormat("tr-TR", { dateStyle: "medium", timeStyle: "short" }).format(new Date(seconds * 1000));
  } catch {
    return "";
  }
}

function formatTry(amount: number) {
  try {
    return new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `${Math.round(amount)} ₺`;
  }
}

type DerivedState = "odeme_bekleniyor" | "hazirlaniyor" | "kargoda" | "tamamlandi" | "iptal" | "basarisiz";
type StateFilter = "all" | DerivedState;

function deriveState(order: Order, requestsByOrderId: Map<string, Request[]>): DerivedState {
  if (order.status === "cancelled" || order.shipping?.status === "cancelled") return "iptal";

  const lastStatus = String(order.payment?.lastStatus ?? "").toUpperCase();
  const lastError = String(order.payment?.lastError ?? "");
  const looksFailed =
    (lastStatus && lastStatus !== "SUCCESS" && lastStatus !== "PENDING" && lastStatus !== "INIT_THREEDS") ||
    /hata|fail|error/i.test(lastError);

  if (order.status === "pending") return looksFailed ? "basarisiz" : "odeme_bekleniyor";

  const hasPhysicalItem = (order.items ?? []).some((item) => item.kind === "product");
  if (hasPhysicalItem) {
    if (order.shipping?.status === "delivered") return "tamamlandi";
    if (order.shipping?.status === "shipped") return "kargoda";
    return "hazirlaniyor";
  }

  const reqs = requestsByOrderId.get(order.id) ?? [];
  if (reqs.some((r) => r.status === "sent")) return "tamamlandi";
  return "hazirlaniyor";
}

function formatAddressText(address?: { addressLine1?: string; city?: string; district?: string; postcode?: string } | null) {
  if (!address) return "";
  const line = String(address.addressLine1 ?? "").trim();
  const city = String(address.city ?? "").trim();
  const district = String(address.district ?? "").trim();
  const postcode = String(address.postcode ?? "").trim();
  return [line, [district, city].filter(Boolean).join(" / "), postcode].filter(Boolean).join(" • ");
}

function shippingStatusLabel(status: ShippingStatus) {
  if (status === "payment_pending") return "Ödeme bekleniyor";
  if (status === "preparing") return "Hazırlanıyor";
  if (status === "shipped") return "Kargoya verildi";
  if (status === "delivered") return "Teslim edildi";
  return "İptal";
}

function stateLabel(state: DerivedState) {
  switch (state) {
    case "odeme_bekleniyor":
      return "Ödeme bekleniyor";
    case "hazirlaniyor":
      return "Hazırlanıyor";
    case "kargoda":
      return "Kargoda";
    case "tamamlandi":
      return "Tamamlandı";
    case "basarisiz":
      return "Başarısız";
    case "iptal":
    default:
      return "İptal";
  }
}

function StateBadge({ state }: { state: DerivedState }) {
  const common = "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold";

  switch (state) {
    case "odeme_bekleniyor":
      return (
        <span
          className={cn(
            common,
            "border-slate-200 bg-slate-50 text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200",
          )}
        > 
          <Clock3 className="h-3.5 w-3.5" /> Ödeme bekleniyor
        </span>
      );
    case "hazirlaniyor":
      return (
        <span
          className={cn(
            common,
            "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-400/20 dark:bg-amber-400/10 dark:text-amber-100",
          )}
        > 
          <CreditCard className="h-3.5 w-3.5" /> Hazırlanıyor
        </span>
      );
    case "kargoda":
      return (
        <span
          className={cn(
            common,
            "border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-400/20 dark:bg-sky-400/10 dark:text-sky-100",
          )}
        >
          <Truck className="h-3.5 w-3.5" /> Kargoda
        </span>
      );
    case "tamamlandi":
      return (
        <span
          className={cn(
            common,
            "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-400/20 dark:bg-emerald-400/10 dark:text-emerald-100",
          )}
        >
          <CheckCircle2 className="h-3.5 w-3.5" /> Tamamlandı
        </span>
      );
    case "basarisiz":
      return (
        <span
          className={cn(
            common,
            "border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-400/20 dark:bg-rose-400/10 dark:text-rose-100",
          )}
        > 
          <XCircle className="h-3.5 w-3.5" /> Başarısız
        </span>
      );
    case "iptal":
    default:
      return (
        <span
          className={cn(
            common,
            "border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-400/20 dark:bg-slate-400/10 dark:text-slate-200",
          )}
        > 
          <Ban className="h-3.5 w-3.5" /> İptal
        </span>
      );
  }
}

export default function AdminNumerolojiOrders() {
  const queryClient = useQueryClient();
  const { sessionQuery, loginMutation, logoutMutation } = useAdminSession();
  const configured = Boolean(sessionQuery.data?.configured);
  const authenticated = Boolean(sessionQuery.data?.authenticated);

  const { theme, setTheme, systemTheme } = useTheme();
  const resolvedTheme = theme === "system" ? systemTheme : theme;
  const isDark = resolvedTheme === "dark";

  const [query, setQuery] = useState("");
  const [stateFilter, setStateFilter] = useState<StateFilter>("all");
  const [shippingDrafts, setShippingDrafts] = useState<Record<string, { status: ShippingStatus; trackingNumber: string; carrier: string }>>({});

  const ordersQuery = useQuery({
    queryKey: ["admin-numeroloji-orders"],
    queryFn: fetchOrders,
    enabled: configured && authenticated,
    staleTime: 10_000,
  });

  const requestsQuery = useQuery({
    queryKey: ["admin-numeroloji-requests"],
    queryFn: fetchRequests,
    enabled: configured && authenticated,
    staleTime: 10_000,
  });

  const requestsByOrderId = useMemo(() => {
    const map = new Map<string, Request[]>();
    for (const r of requestsQuery.data?.items ?? []) {
      const k = String(r.orderId ?? "");
      if (!k) continue;
      const list = map.get(k) ?? [];
      list.push(r);
      map.set(k, list);
    }
    return map;
  }, [requestsQuery.data?.items]);

  const orderStateCounts = useMemo(() => {
    const counts: Record<DerivedState, number> = {
      odeme_bekleniyor: 0,
      hazirlaniyor: 0,
      kargoda: 0,
      tamamlandi: 0,
      iptal: 0,
      basarisiz: 0,
    };

    for (const order of ordersQuery.data?.items ?? []) {
      const state = deriveState(order, requestsByOrderId);
      counts[state] += 1;
    }

    return counts;
  }, [ordersQuery.data?.items, requestsByOrderId]);

  const filteredOrders = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = ordersQuery.data?.items ?? [];

    return all.filter((o) => {
      const state = deriveState(o, requestsByOrderId);
      if (stateFilter !== "all" && state !== stateFilter) return false;

      if (!q) return true;

      const hay = `${o.id} ${o.customer?.firstName ?? ""} ${o.customer?.lastName ?? ""} ${o.customer?.email ?? ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [ordersQuery.data?.items, query, requestsByOrderId, stateFilter]);

  const cancelMutation = useMutation({
    mutationFn: cancelOrder,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-orders"] });
    },
  });

  const paidMutation = useMutation({
    mutationFn: markPaid,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-orders"] });
    },
  });

  const shippingMutation = useMutation({
    mutationFn: updateShipping,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-orders"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteOrder,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-orders"] }),
        queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-requests"] }),
      ]);
    },
  });

  if (!configured) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <Card className="p-5">
          <div className="text-sm font-medium">Admin ayarları yapılmamış</div>
          <div className="mt-1 text-sm text-muted-foreground">Sunucu tarafında admin girişi ayarlanmalı.</div>
        </Card>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <AdminLoginCard
          configured={configured}
          onLogin={(args) => loginMutation.mutate(args)}
          isLoading={loginMutation.isPending}
          error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh w-full">
      <NumerolojiAdminSidebar
        mode="links"
        isDark={isDark}
        onToggleTheme={() => setTheme(isDark ? "light" : "dark")}
      />

      <div className="flex min-w-0 flex-1 flex-col bg-slate-100/60 dark:bg-slate-900/30">
        <header className="flex h-16 items-center justify-between border-b border-slate-200/70 bg-white/70 px-4 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/40 sm:px-6">
          <div className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-50 sm:text-lg">Siparişler</div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              className="rounded-full"
              size="sm"
              onClick={() => logoutMutation.mutate()}
              disabled={logoutMutation.isPending}
            >
              Çıkış
            </Button>
          </div>
        </header>

        <div className="min-w-0 flex-1 p-4 sm:p-6">
          <div className="mx-auto w-full max-w-6xl">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <div className="text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-50">Sipariş listesi</div>
                <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  WooCommerce benzeri akış: ödeme bekleniyor / hazırlanıyor / tamamlandı.
                </div>
              </div>

              <Button asChild variant="outline" className="rounded-full">
                <Link to="/admin/numeroloji">Dashboard</Link>
              </Button>
            </div>

            <div className="mt-6 space-y-3">
              <div className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">Filtre</div>

              <div className="flex flex-wrap gap-2">
                {([
                  "all",
                  "odeme_bekleniyor",
                  "hazirlaniyor",
                  "kargoda",
                  "tamamlandi",
                  "basarisiz",
                  "iptal",
                ] as const).map((value) => {
                  const isActive = stateFilter === value;
                  const count = value === "all"
                    ? ordersQuery.data?.items?.length ?? 0
                    : orderStateCounts[value];

                  return (
                    <Button
                      key={value}
                      type="button"
                      size="sm"
                      variant={isActive ? "default" : "outline"}
                      className="rounded-full"
                      onClick={() => setStateFilter(value)}
                    >
                      {value === "all" ? "Tümü" : stateLabel(value)}
                      <span className="ml-2 rounded-full bg-black/10 px-2 py-0.5 text-[11px] dark:bg-white/10">{count}</span>
                    </Button>
                  );
                })}
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  Gösterilen: {filteredOrders.length}
                </div>
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Sipariş no / müşteri / e-posta"
                  className="h-10 w-full sm:w-[360px]"
                />
              </div>
            </div>

            <div className="mt-6">

              {ordersQuery.isLoading || requestsQuery.isLoading ? (
                <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-600 shadow-sm dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300">
                  Yükleniyor...
                </div>
              ) : null}

              {ordersQuery.isError ? (
                <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 p-5 text-sm text-rose-800 dark:text-rose-200">
                  {(ordersQuery.error as Error).message}
                </div>
              ) : null}

              {requestsQuery.isError ? (
                <div className="mt-3 rounded-2xl border border-rose-500/30 bg-rose-500/10 p-5 text-sm text-rose-800 dark:text-rose-200">
                  {(requestsQuery.error as Error).message}
                </div>
              ) : null}

              {!ordersQuery.isLoading && !ordersQuery.isError ? (
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
                  <div className="overflow-x-auto">
                    <table className="min-w-[980px] w-full border-collapse text-left text-sm">
                      <thead className="border-b border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950/40">
                    <tr>
                      <th className="px-5 py-4 text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">Sipariş</th>
                      <th className="px-5 py-4 text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">Müşteri</th>
                      <th className="px-5 py-4 text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">Durum</th>
                      <th className="px-5 py-4 text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">Fatura & Adres</th>
                      <th className="px-5 py-4 text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">Kargo</th>
                      <th className="px-5 py-4 text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">Tutar</th>
                      <th className="px-5 py-4 text-right text-xs font-semibold uppercase tracking-wider text-slate-400">İşlem</th>
                    </tr>
                  </thead>
                      <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                    {filteredOrders.map((o) => {
                      const state = deriveState(o, requestsByOrderId);
                      const reqs = requestsByOrderId.get(o.id) ?? [];
                      const firstReq = reqs[0] ?? null;
                      const shippingSource = o.shipping ?? {
                        status: (o.status === "pending" ? "payment_pending" : "preparing") as ShippingStatus,
                        trackingNumber: "",
                        carrier: "",
                        updatedAt: o.paidAt ?? o.createdAt,
                      };
                      const draft = shippingDrafts[o.id] ?? {
                        status: shippingSource.status,
                        trackingNumber: shippingSource.trackingNumber ?? "",
                        carrier: shippingSource.carrier ?? "",
                      };
                      const hasPhysicalItem = (o.items ?? []).some((item) => item.kind === "product");
                      const billingText = formatAddressText(o.customer?.billingAddress ?? null) || formatAddressText(o.customer);
                      const shippingText = formatAddressText(o.customer?.shippingAddress ?? null) || formatAddressText(o.customer);

                      return (
                        <tr key={o.id} className="align-top">
                          <td className="px-5 py-4">
                            <Link to={`/admin/numeroloji/siparisler/${encodeURIComponent(o.id)}`} className="font-semibold text-slate-900 underline-offset-2 hover:underline dark:text-slate-50">#{o.id}</Link>
                            <div className="mt-1 text-xs text-slate-600 dark:text-slate-400">{formatDate(o.createdAt)}</div>
                            {o.paidAt ? (
                              <div className="mt-1 text-xs text-slate-600 dark:text-slate-400">Ödendi: {formatDate(o.paidAt)}</div>
                            ) : null}
                          </td>
                          <td className="px-5 py-4">
                            <div className="font-medium text-slate-900 dark:text-slate-50">
                              {(o.customer?.firstName ?? "").trim()} {(o.customer?.lastName ?? "").trim()}
                            </div>
                            <div className="mt-1 text-xs text-slate-600 dark:text-slate-400">{o.customer?.email ?? ""}</div>
                          </td>
                          <td className="px-5 py-4">
                            <StateBadge state={state} />
                            {state === "basarisiz" && o.payment?.lastError ? (
                              <div className="mt-2 max-w-[320px] text-xs text-rose-700 dark:text-rose-200/90">
                                {o.payment.lastError}
                              </div>
                            ) : null}
                            {firstReq ? (
                              <div className="mt-2 text-xs text-slate-600 dark:text-slate-400">
                                İstek: <Link className="underline underline-offset-2" to={`/admin/numeroloji/${encodeURIComponent(firstReq.id)}`}>{firstReq.id}</Link>
                              </div>
                            ) : null}
                          </td>
                          <td className="px-5 py-4 text-xs text-slate-600 dark:text-slate-300">
                            <div className="space-y-1">
                              <div><span className="font-semibold">Telefon:</span> {o.customer?.phone || "—"}</div>
                              <div><span className="font-semibold">TCKN:</span> {o.customer?.identityNumber || "—"}</div>
                              <div><span className="font-semibold">Fatura:</span> {billingText || "—"}</div>
                              <div><span className="font-semibold">Teslimat:</span> {shippingText || "—"}</div>
                            </div>
                          </td>
                          <td className="px-5 py-4 text-xs text-slate-600 dark:text-slate-300">
                            {hasPhysicalItem ? (
                              <div className="space-y-2">
                                <div className="font-semibold text-slate-900 dark:text-slate-50">{shippingStatusLabel(draft.status)}</div>
                                <select
                                  value={draft.status}
                                  onChange={(e) => {
                                    const value = e.target.value as ShippingStatus;
                                    setShippingDrafts((prev) => ({
                                      ...prev,
                                      [o.id]: {
                                        ...draft,
                                        status: value,
                                      },
                                    }));
                                  }}
                                  className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-xs dark:border-slate-700 dark:bg-slate-900"
                                >
                                  <option value="payment_pending">Ödeme bekleniyor</option>
                                  <option value="preparing">Hazırlanıyor</option>
                                  <option value="shipped">Kargoya verildi</option>
                                  <option value="delivered">Teslim edildi</option>
                                  <option value="cancelled">İptal</option>
                                </select>
                                <Input
                                  value={draft.trackingNumber}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setShippingDrafts((prev) => ({
                                      ...prev,
                                      [o.id]: {
                                        ...draft,
                                        trackingNumber: value,
                                      },
                                    }));
                                  }}
                                  placeholder="Takip no"
                                  className="h-9 text-xs"
                                />
                                <Input
                                  value={draft.carrier}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setShippingDrafts((prev) => ({
                                      ...prev,
                                      [o.id]: {
                                        ...draft,
                                        carrier: value,
                                      },
                                    }));
                                  }}
                                  placeholder="Kargo firması"
                                  className="h-9 text-xs"
                                />
                                <Button
                                  variant="outline"
                                  className="h-8 rounded-full text-xs"
                                  onClick={() =>
                                    shippingMutation.mutate({
                                      id: o.id,
                                      status: draft.status,
                                      trackingNumber: draft.trackingNumber.trim() || undefined,
                                      carrier: draft.carrier.trim() || undefined,
                                    })
                                  }
                                  disabled={shippingMutation.isPending}
                                >
                                  Kargoyu güncelle
                                </Button>
                              </div>
                            ) : (
                              <span className="text-slate-500 dark:text-slate-400">Fiziksel ürün yok</span>
                            )}
                          </td>
                          <td className="px-5 py-4 font-semibold text-slate-900 dark:text-slate-50">{formatTry(o.totalTry)}</td>
                          <td className="px-5 py-4 text-right">
                            <div className="flex flex-col items-end gap-2">
                              <Button asChild variant="outline" className="h-9 rounded-full">
                                <Link to={`/admin/numeroloji/siparisler/${encodeURIComponent(o.id)}`}>Detaya git</Link>
                              </Button>

                              {o.status === "pending" ? (
                                <Button
                                  variant="outline"
                                  className="h-9 rounded-full"
                                  onClick={() => paidMutation.mutate(o.id)}
                                  disabled={paidMutation.isPending}
                                >
                                  Ödendi işaretle
                                </Button>
                              ) : null}

                              {o.status !== "cancelled" ? (
                                <Button
                                  variant="outline"
                                  className="h-9 rounded-full border-rose-500/30 text-rose-700 hover:bg-rose-500/10 dark:text-rose-200"
                                  onClick={() => cancelMutation.mutate(o.id)}
                                  disabled={cancelMutation.isPending}
                                >
                                  İptal et
                                </Button>
                              ) : null}

                              <Button
                                variant="outline"
                                className="h-9 rounded-full border-slate-300 text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                                onClick={() => deleteMutation.mutate(o.id)}
                                disabled={deleteMutation.isPending}
                              >
                                Sil
                              </Button>

                              {!reqs.length && o.status === "paid" ? (
                                <div className="text-xs text-slate-600 dark:text-slate-400">
                                  Müşteri formu doldurunca istek listesine düşer.
                                </div>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}

                        {!filteredOrders.length ? (
                          <tr>
                            <td colSpan={7} className="px-5 py-10 text-center text-sm text-slate-500 dark:text-slate-300">
                              Sipariş bulunamadı.
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
